/**
 * Select — custom dark-themed dropdown that replaces the native <select>.
 *
 * Why: native <select> uses the OS popup which is white-on-light on most
 * platforms and unreadable against our dark portal. This component renders
 * a floating anchored menu with search, keyboard navigation, type-ahead,
 * outside-click dismiss, and a portaled positioning so it never gets
 * clipped by ancestor `overflow:hidden`.
 *
 * API mirrors native select: `value` + `onChange(value)`. Options can be
 * strings OR { value, label, hint?, disabled? }.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Check, Search as SearchIcon } from "lucide-react";

function normalize(opts) {
  return (opts || []).map((o) =>
    typeof o === "object" && o !== null ? { ...o } : { value: o, label: String(o) }
  );
}

export default function Select({
  value,
  onChange,
  options,
  placeholder = "Select…",
  searchable = true,
  disabled = false,
  className = "",
  menuClassName = "",
  testid,
}) {
  const opts = useMemo(() => normalize(options), [options]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const anchorRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);

  const selected = opts.find((o) => String(o.value) === String(value));
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return opts;
    return opts.filter(
      (o) =>
        String(o.label).toLowerCase().includes(q) ||
        String(o.value).toLowerCase().includes(q) ||
        (o.hint && String(o.hint).toLowerCase().includes(q))
    );
  }, [opts, query]);

  // Anchor the portal menu under the trigger
  useEffect(() => {
    if (!open) return;
    const update = () => {
      const r = anchorRef.current?.getBoundingClientRect();
      if (!r) return;
      const viewportH = window.innerHeight;
      const preferBelow = viewportH - r.bottom > 260 || r.top < 260;
      setPos({
        top: preferBelow ? r.bottom + window.scrollY + 6 : r.top + window.scrollY - 6,
        left: r.left + window.scrollX,
        width: r.width,
        placement: preferBelow ? "below" : "above",
      });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open]);

  // Outside click
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (
        !anchorRef.current?.contains(e.target) &&
        !menuRef.current?.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Focus search on open
  useEffect(() => {
    if (open && searchable) {
      const id = setTimeout(() => searchRef.current?.focus(), 30);
      return () => clearTimeout(id);
    }
  }, [open, searchable]);

  const pick = (opt) => {
    if (opt?.disabled) return;
    onChange?.(opt?.value ?? "");
    setOpen(false);
    setQuery("");
    anchorRef.current?.focus();
  };

  const onKey = (e) => {
    if (!open && (e.key === "Enter" || e.key === " " || e.key === "ArrowDown")) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === "Escape") { e.preventDefault(); setOpen(false); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(filtered.length - 1, h + 1)); return; }
    if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(0, h - 1)); return; }
    if (e.key === "Enter") { e.preventDefault(); pick(filtered[highlight]); return; }
  };

  useEffect(() => { if (open) setHighlight(0); }, [query, open]);

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={onKey}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={testid}
        className={
          "w-full flex items-center justify-between gap-2 h-10 px-3 rounded-md " +
          "bg-[var(--portal-bg-base,#05060a)] border " +
          (open
            ? "border-[var(--accent,#00e5ff)] ring-2 ring-[var(--accent-glow,rgba(0,229,255,0.14))]"
            : "border-[var(--portal-border,rgba(255,255,255,0.1))] hover:border-[var(--portal-border-strong,rgba(255,255,255,0.2))]") +
          " text-[13px] text-left transition " +
          (disabled ? "opacity-50 cursor-not-allowed " : "cursor-pointer ") +
          className
        }
      >
        <span className={selected ? "text-[var(--portal-text-primary,#f2f3f5)] truncate" : "text-[var(--portal-text-tertiary,#5c616b)] truncate"}>
          {selected ? (selected.label || selected.value) : placeholder}
        </span>
        <ChevronDown size={14} className={`shrink-0 transition ${open ? "rotate-180 text-[var(--accent,#00e5ff)]" : "text-[var(--portal-text-secondary,#8a8f98)]"}`} />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            data-testid={testid ? `${testid}-menu` : undefined}
            style={{
              position: "absolute",
              top: pos.placement === "above" ? undefined : pos.top,
              bottom: pos.placement === "above" ? window.innerHeight - pos.top + 6 : undefined,
              left: pos.left,
              width: pos.width,
              zIndex: 9999,
            }}
            className={
              "rounded-lg border border-[var(--portal-border-strong)] " +
              "bg-[var(--portal-bg-surface)] shadow-[var(--portal-shadow-lg)] overflow-hidden " +
              menuClassName
            }
          >
            {searchable && opts.length > 6 && (
              <div className="flex items-center gap-2 px-3 h-10 border-b border-[var(--portal-border-subtle,rgba(255,255,255,0.06))]">
                <SearchIcon size={13} className="text-[var(--portal-text-tertiary,#5c616b)]" />
                <input
                  ref={searchRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onKey}
                  placeholder="Search…"
                  className="flex-1 bg-transparent outline-none text-[13px] text-[var(--portal-text-primary,#f2f3f5)] placeholder:text-[var(--portal-text-tertiary,#5c616b)]"
                  data-testid={testid ? `${testid}-search` : undefined}
                />
                {query && (
                  <button type="button" onClick={() => setQuery("")} className="text-[10px] font-mono text-[var(--portal-text-secondary,#8a8f98)] hover:text-white">clear</button>
                )}
              </div>
            )}
            <div className="max-h-[260px] overflow-y-auto py-1" role="listbox">
              {filtered.length === 0 ? (
                <div className="px-3 py-4 text-center text-[12px] text-[var(--portal-text-secondary,#8a8f98)]">
                  No matches
                </div>
              ) : (
                filtered.map((opt, i) => {
                  const isSel = String(opt.value) === String(value);
                  const isHi = i === highlight;
                  return (
                    <button
                      key={String(opt.value) + i}
                      type="button"
                      role="option"
                      aria-selected={isSel}
                      disabled={opt.disabled}
                      onMouseEnter={() => setHighlight(i)}
                      onClick={() => pick(opt)}
                      className={
                        "w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-[13px] transition " +
                        (opt.disabled
                          ? "opacity-40 cursor-not-allowed "
                          : "cursor-pointer ") +
                        (isHi
                          ? "bg-[var(--portal-bg-hover,rgba(255,255,255,0.04))] text-[var(--portal-text-primary,#f2f3f5)]"
                          : "text-[var(--portal-text-secondary,#8a8f98)]")
                      }
                      data-testid={testid ? `${testid}-opt-${String(opt.value)}` : undefined}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="truncate text-[var(--portal-text-primary,#f2f3f5)]">{opt.label}</div>
                        {opt.hint && (
                          <div className="truncate text-[10px] font-mono text-[var(--portal-text-tertiary,#5c616b)] mt-0.5">
                            {opt.hint}
                          </div>
                        )}
                      </div>
                      {isSel && <Check size={14} className="text-[var(--accent,#00e5ff)] shrink-0" />}
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
